import { useNavigate } from "react-router-dom";
import { useLocale } from "@hooks/useLocale";
import { useCurrentWell } from "@hooks/useCurrentWell";
import { useAppDispatch } from "@app/hooks";
import { setActiveEvent } from "@app/slices/wellContextSlice";
import { QueryState } from "@components/ui/QueryState";
import { useGetEventReportsQuery, useGetPlanVsActualQuery, useGetNptRollupQuery } from "@api/pApi";
import { fmtPct } from "@lib/format";
import { useUnits } from "@units/useUnits";
import type { EventOut, MessageEventTypeKey } from "./eventType";
import { eventTypeKey } from "./eventType";
import { STATUS_KEY } from "./wellLabels";

/**
 * Well / Wellbore record hub. Header comes from Oxbo-M (the register); the
 * event/job selector and the three doorway cards each surface ONE real stat
 * from Oxbo-P (latest report day, AFE % spent, NPT %) — server-computed, shown
 * verbatim. Field-user-first, no ownership tint by default.
 */
export function WellRecord() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { well, events, activeEvent, isLoading, isError } = useCurrentWell();
  // Well-register TD is stored in metres; convert it to the viewer's display
  // profile with a dynamic label (same pipeline as every other length).
  const { convertField, fmt } = useUnits(activeEvent?.unit_profile_key);

  if (isLoading || isError || !well) {
    return (
      <div>
        <div className="page-head">
          <h1>{t("nav.wellRecord")}</h1>
        </div>
        <div className="card">
          <QueryState isLoading={isLoading} isError={isError} isEmpty={!well}>
            <div className="empty-state">{t("well.selectPrompt")}</div>
          </QueryState>
        </div>
      </div>
    );
  }

  const evKey: MessageEventTypeKey | null = activeEvent ? eventTypeKey(activeEvent) : null;

  return (
    <div>
      <div className="page-head">
        <h1>{well.commonName}</h1>
        <p className="subtitle">
          {well.field} · {well.operator}
          {evKey ? ` · ${t(evKey)}` : ""}
        </p>
      </div>

      {/* Header — from Oxbo-M */}
      <div className="card" data-owner="m" style={{ marginBottom: 14 }}>
        <div className="kv-grid">
          <KV k={t("well.header.uwi")} v={well.uwi} mono />
          <KV k={t("well.header.api")} v={well.apiNo ?? "—"} mono />
          <KV k={t("well.header.field")} v={well.field} />
          <KV k={t("well.header.operator")} v={well.operator} />
          <KV k={t("well.header.formation")} v={well.formation ?? "—"} />
          <KV
            k={t("well.header.td")}
            v={fmt(convertField("daily_report", "md_current", well.totalDepthM, "metric"))}
            mono
          />
          <KV
            k={t("well.header.status")}
            v={well.status ? t(STATUS_KEY[well.status]) : (well.statusRaw ?? "—")}
          />
        </div>
      </div>

      {/* Event / job selector — from Oxbo-P */}
      <div className="card" data-owner="p" style={{ marginBottom: 14 }}>
        <h3>{t("well.events")}</h3>
        {events.length === 0 ? (
          <div className="empty-state">{t("well.event.noEvents")}</div>
        ) : (
          <div className="event-chips" role="group" aria-label={t("well.events")}>
            {events.map((ev) => (
              <button
                key={ev.id}
                type="button"
                className={`event-chip${ev.id === activeEvent?.id ? " active" : ""}`}
                onClick={() => dispatch(setActiveEvent(ev.id))}
              >
                <span className="event-code">{ev.event_code ?? "—"}</span>
                {t(eventTypeKey(ev))}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Doorway cards — one real Oxbo-P stat each */}
      {activeEvent ? (
        <div className="doorways">
          <DailyDoorway event={activeEvent} onOpen={() => navigate("/daily")} />
          <CostDoorway event={activeEvent} onOpen={() => navigate("/cost-afe")} />
          <NptDoorway event={activeEvent} onOpen={() => navigate("/time-npt")} />
        </div>
      ) : null}
    </div>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="kv">
      <span className="kv-k">{k}</span>
      <span className={`kv-v${mono ? " mono" : ""}`}>{v}</span>
    </div>
  );
}

/** Daily-report doorway: shows the latest report day (from P). */
function DailyDoorway({ event, onOpen }: { event: EventOut; onOpen: () => void }) {
  const { t } = useLocale();
  const { data: reports = [] } = useGetEventReportsQuery(event.id);
  const latest = reports[0]; // handlers return newest-first
  return (
    <button type="button" className="doorway" data-owner="p" onClick={onOpen}>
      <span className="doorway-title">{t("well.doorway.dailyReport")}</span>
      <span className="doorway-stat">
        {latest?.report_no != null
          ? t("well.doorway.latestReport", { n: latest.report_no })
          : t("common.na")}
      </span>
      <span className="doorway-open">{t("well.doorway.open")} →</span>
    </button>
  );
}

/** Cost doorway: shows AFE % spent (from P plan-vs-actual). */
function CostDoorway({ event, onOpen }: { event: EventOut; onOpen: () => void }) {
  const { t, locale } = useLocale();
  const { data } = useGetPlanVsActualQuery(event.id);
  const pct = data && data.afe_total > 0 ? (data.actual_total / data.afe_total) * 100 : null;
  return (
    <button type="button" className="doorway" data-owner="p" onClick={onOpen}>
      <span className="doorway-title">{t("well.doorway.costAfe")}</span>
      <span className="doorway-stat">
        {pct != null ? t("well.doorway.spent", { pct: fmtPct(pct, locale) }) : t("common.na")}
      </span>
      <span className="doorway-open">{t("well.doorway.open")} →</span>
    </button>
  );
}

/** NPT doorway: shows non-productive-time % (from P npt-rollup). */
function NptDoorway({ event, onOpen }: { event: EventOut; onOpen: () => void }) {
  const { t, locale } = useLocale();
  const { data } = useGetNptRollupQuery(event.id);
  return (
    <button type="button" className="doorway" data-owner="p" onClick={onOpen}>
      <span className="doorway-title">{t("well.doorway.timeNpt")}</span>
      <span className="doorway-stat">
        {data?.npt_pct != null
          ? t("well.doorway.nptPct", { pct: fmtPct(data.npt_pct, locale, 1) })
          : t("common.na")}
      </span>
      <span className="doorway-open">{t("well.doorway.open")} →</span>
    </button>
  );
}
